import React, { useState } from 'react';
import {
  FolderKanban, AlertTriangle, Trash2, RefreshCw, Check, X,
  Layers, Target, FileText, ArrowRight, Undo2, Info
} from 'lucide-react';

// ==========================================
// WBS GENERATION PREVIEW MODAL
// ==========================================

const WBSPreviewModal = ({
  isOpen,
  onClose,
  onConfirm,
  onReset,
  requirementsCount,
  existingWBSCount,
  baselineName,
  loading,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  
  if (!isOpen) return null;

  const hasExistingWBS = existingWBSCount > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden" data-testid="wbs-preview-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <FolderKanban className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Generate Work Breakdown Structure</h2>
              <p className="text-purple-100 text-sm mt-0.5">
                Baseline: {baselineName || 'New Baseline'}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-purple-600">{requirementsCount}</p>
              <p className="text-sm text-gray-600 mt-1">Requirements</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <ArrowRight className="w-6 h-6 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Will generate WBS nodes</p>
            </div>
          </div>

          {/* Warning if existing WBS */}
          {hasExistingWBS && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">Existing WBS will be replaced</p>
                  <p className="text-sm text-amber-700 mt-1">
                    You have {existingWBSCount} existing WBS nodes. Generating a new WBS will replace them.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* What will happen */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">What happens next:</p>
                <ul className="list-disc list-inside space-y-0.5 text-blue-700">
                  <li>Work packages created for each requirement</li>
                  <li>Project phases added (Initiation, Planning, Closure)</li>
                  <li>You can edit or delete WBS nodes after generation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="border-t p-4 bg-gray-50 flex items-center justify-between">
          {/* Reset Option */}
          {hasExistingWBS && (
            <button
              onClick={() => setConfirmDelete(true)}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              data-testid="wbs-reset-btn"
            >
              <Trash2 className="w-4 h-4" />
              Reset WBS
            </button>
          )}
          {!hasExistingWBS && <div />}
          
          {/* Main Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading || requirementsCount === 0}
              className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="wbs-confirm-btn"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Generate WBS
            </button>
          </div>
        </div>

        {/* Delete Confirmation Overlay */}
        {confirmDelete && (
          <div className="absolute inset-0 bg-white/95 flex items-center justify-center p-6">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Reset WBS?</h3>
              <p className="text-gray-600 mb-6">
                This will delete all {existingWBSCount} existing WBS nodes. This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onReset?.();
                    setConfirmDelete(false);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  data-testid="wbs-confirm-reset-btn"
                >
                  Yes, Reset WBS
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==========================================
// REVERT TO BASELINE MODAL
// ==========================================

const RevertBaselineModal = ({
  isOpen,
  onClose,
  onRevert,
  baselines = [],
  currentBaselineId,
  loading,
}) => {
  const [selectedBaseline, setSelectedBaseline] = useState(null);
  
  if (!isOpen) return null;

  const previousBaselines = baselines.filter(b => 
    b.id !== currentBaselineId && b.status === 'approved'
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden" data-testid="revert-baseline-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-6 text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Undo2 className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Revert to Previous Baseline</h2>
              <p className="text-blue-100 text-sm mt-0.5">
                Restore scope from an approved baseline
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {previousBaselines.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Info className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600">No approved baselines available to revert to.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">
                Select a baseline to restore. Current draft changes will be discarded.
              </p>
              {previousBaselines.map((baseline) => (
                <button
                  key={baseline.id}
                  onClick={() => setSelectedBaseline(baseline)}
                  className={`w-full p-4 border rounded-xl text-left transition-all ${
                    selectedBaseline?.id === baseline.id
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{baseline.name}</p>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {baseline.requirement_count || 0} requirements • {baseline.wbs_node_count || 0} WBS nodes
                      </p>
                    </div>
                    {selectedBaseline?.id === baseline.id && (
                      <Check className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="border-t p-4 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={() => onRevert?.(selectedBaseline)}
            disabled={loading || !selectedBaseline}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="revert-confirm-btn"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Undo2 className="w-4 h-4" />
            )}
            Revert
          </button>
        </div>
      </div>
    </div>
  );
};

export { WBSPreviewModal, RevertBaselineModal };
export default WBSPreviewModal;
